import React from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFSelectField, RHFTextField } from '../../../common/hook-form';
import AbdMerchantFilter from './AbdMerchantFilter';
import AbdCourierFilter from './AbdCourierFilter';
import { ATTEMPT_OPTIONS, DOCUMENTS_OPTIONS, MISS_CATEGORY_OPTIONS, getDefaultDateRange } from './constants';

// Static label above field — matches sample mockup `.filter-field label`.
function FieldLabel({ children }) {
    return (
        <Typography
            sx={{
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#888',
                fontWeight: 600,
                mb: 0.5,
                display: 'block',
                lineHeight: 1.2,
            }}
        >{children}</Typography>
    );
}

export default function FilterBar({
    show = ['merchant', 'courier', 'awb', 'city', 'attempts', 'missCategory', 'dateRange'],
    onApply,
    onReset,
    initial = {},
    // Options for the opt-in 'reason' field: [{ value, label }].
    reasonOptions = [],
}) {
    const def = getDefaultDateRange();
    const methods = useForm({
        defaultValues: {
            merchant: '',
            courier: null,
            awb: '',
            reference: '',
            city: '',
            state: '',
            attempts: '',
            missCategory: '',
            documents: '',
            editedBy: '',
            reason: '',
            fromDate: def.fromDate,
            toDate: def.toDate,
            ...initial,
        },
    });
    const { handleSubmit, reset, watch } = methods;
    const documents = watch('documents');
    const visible = (k) => show.includes(k);

    const submit = (data) => {
        onApply({
            merchant: data.merchant || '',
            courier: data.courier?.id || data.courier || '',
            awb: data.awb,
            reference: data.reference,
            city: data.city,
            state: data.state,
            attempts: data.attempts,
            missCategory: data.missCategory,
            documents: data.documents,
            ...(visible('editedBy') && { editedBy: data.editedBy || '' }),
            ...(visible('reason') && { reason: data.reason || '' }),
            fromDate: data.fromDate || null,
            toDate: data.toDate || null,
        });
    };

    const resetAll = () => {
        const fresh = {
            merchant: '',
            courier: null,
            awb: '',
            reference: '',
            city: '',
            state: '',
            attempts: '',
            missCategory: '',
            documents: '',
            editedBy: '',
            reason: '',
            fromDate: def.fromDate,
            toDate: def.toDate,
        };
        reset(fresh);
        onReset?.();
    };

    // Mockup `.filter-row`: flex layout, each field `flex: 1; min-width: 140px`,
    // input padding 8px 10px, font-size 12px, label 10px uppercase #888.
    // Replicated below via flex sizing + size='small' + sx overrides on
    // controls. Date Range is fixed-width so it doesn't stretch a wrapped row.
    const fieldSx = { flex: '1 1 140px', minWidth: 140 };
    const dateSx = { flex: '0 0 260px', minWidth: 260 };
    // Override MUI floating-label outlined input to look like sample's plain
    // <input>: hide the floating label entirely (we render a static label
    // above), tighten padding + font, simple #ccc border.
    const inputSx = {
        // Force every filter control (TextField, Select, Autocomplete) to a
        // single uniform height so Attempts / Miss Category Selects line up
        // with the AWB / City TextFields and the Merchant / Courier
        // Autocompletes on the same row.
        '& .MuiOutlinedInput-root': {
            fontSize: '12px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            minHeight: '33px',
            height: '33px',
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 10px',
            fontSize: '12px',
            color: '#333',
            height: 'auto',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ccc',
            // No floating label: hide the legend cutout in the border so the
            // top border is one continuous line.
            top: 0,
            '& legend': { display: 'none' },
        },
        '& .MuiInputLabel-root': {
            // Hide the built-in floating label — replaced by FieldLabel above.
            display: 'none',
        },
        // RHFSelectField wraps Select in a FormControl with FormHelperText
        // that reserves a 19-20px gap below even when empty. Collapse it so
        // Attempts / Miss Category align with the other filter fields.
        '& .MuiFormHelperText-root': {
            display: 'none',
        },
        // Force placeholder visible (MUI hides it by default unless the
        // floating label is shrunk; we removed the label so it never shrinks).
        // Outlined inputs need !important to override MUI's `:not(:focus)` rule.
        '& input::placeholder, & .MuiInputBase-input::placeholder': {
            opacity: '1 !important',
            color: '#999',
            fontSize: '12px',
        },
        '& .MuiOutlinedInput-input::placeholder': {
            opacity: '1 !important',
            color: '#999',
        },
        // RHFSelectField renders its display via Typography inside renderValue.
        // Force its size to match the input placeholder text (12px).
        '& .MuiSelect-select .MuiTypography-root': {
            fontSize: '12px',
            color: '#999',
        },
        // MUI Select with size='small' is taller than the matching TextField
        // (38px vs 33px). Force same height + padding so Attempts /
        // MissCategory align with the other filter fields on the same row.
        '& .MuiSelect-select': {
            padding: '8px 32px 8px 10px !important',
            minHeight: '0 !important',
            height: 'auto',
            lineHeight: '17px',
            boxSizing: 'border-box',
        },
        // Autocomplete adornment + dropdown arrow alignment.
        '& .MuiAutocomplete-inputRoot': {
            padding: '0 35px 0 4px !important',
        },
        '& .MuiAutocomplete-input': {
            padding: '8px 10px !important',
        },
    };

    return (
        <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <FormProvider methods={methods} onSubmit={handleSubmit(submit)}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            alignItems: 'flex-end',
                        }}
                    >
                        {visible('merchant') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Merchant</FieldLabel>
                                <AbdMerchantFilter fullWidth size='small' label='' placeholder='Select Merchant' />
                            </Box>
                        )}
                        {visible('courier') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Courier</FieldLabel>
                                <AbdCourierFilter filterList='B2B' fullWidth size='small' label='' placeholder='Select Courier' />
                            </Box>
                        )}
                        {visible('reference') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Order Number</FieldLabel>
                                <RHFTextField name='reference' label='' placeholder='Order Number' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('awb') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name='awb' label='' placeholder='AWB Number' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('city') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name='city' label='' placeholder='City' fullWidth size='small' InputLabelProps={{ shrink: true }} disabled />
                            </Box>
                        )}
                        {visible('state') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name='state' label='' placeholder='State' fullWidth size='small' InputLabelProps={{ shrink: true }} disabled />
                            </Box>
                        )}
                        {visible('attempts') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Attempts</FieldLabel>
                                <RHFSelectField name='attempts' label='' placeholder='All' width='100%' size='small' displayEmpty options={ATTEMPT_OPTIONS} />
                            </Box>
                        )}
                        {visible('missCategory') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Miss Category</FieldLabel>
                                <RHFSelectField name='missCategory' label='' placeholder='All' width='100%' size='small' displayEmpty options={MISS_CATEGORY_OPTIONS} />
                            </Box>
                        )}
                        {visible('documents') && (
                            <Box sx={{
                                ...fieldSx, ...inputSx,
                                // A chosen value reads dark; the empty placeholder stays grey.
                                ...(documents && { '& .MuiSelect-select.MuiSelect-select .MuiTypography-root': { color: '#1e293b' } }),
                            }}>
                                <FieldLabel>Documents</FieldLabel>
                                <RHFSelectField name='documents' label='' placeholder='All' width='100%' size='small' displayEmpty options={DOCUMENTS_OPTIONS} />
                            </Box>
                        )}
                        {visible('editedBy') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Edited By</FieldLabel>
                                <RHFTextField name='editedBy' label='' placeholder='Name or email' fullWidth size='small' InputLabelProps={{ shrink: true }} />
                            </Box>
                        )}
                        {visible('reason') && (
                            <Box sx={{ ...fieldSx, ...inputSx }}>
                                <FieldLabel>Reason</FieldLabel>
                                <RHFSelectField name='reason' label='' placeholder='All' width='100%' size='small' displayEmpty
                                    options={[{ value: '', label: 'All' }, ...reasonOptions]} />
                            </Box>
                        )}
                        {visible('dateRange') && (
                            <Box sx={{ ...dateSx, ...inputSx }}>
                                <FieldLabel>Date Range</FieldLabel>
                                <RHFDateRangePickerField name='fromDate' label='' placeholder='Select date range' size='small' maxDate={new Date()} />
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                            <Button
                                type='submit' variant='contained' size='small'
                                sx={{
                                    bgcolor: '#1565c0',
                                    color: '#fff',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    px: 2.5,
                                    py: 1,
                                    '&:hover': { bgcolor: '#0d47a1' },
                                }}
                            >Filter</Button>
                            <Button
                                type='button' variant='contained' size='small' onClick={resetAll}
                                sx={{
                                    bgcolor: '#ef4444',
                                    color: '#fff',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    px: 2.5,
                                    py: 1,
                                    '&:hover': { bgcolor: '#dc2626' },
                                }}
                            >Reset</Button>
                        </Box>
                    </Box>
                </FormProvider>
            </CardContent>
        </Card>
    );
}
